/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { Lead } from 'types/entities/Lead'
import { Offer } from 'types/entities/Offer'
import { OfferCalculation } from 'types/entities/OfferCalculation'
import { Reservation } from 'types/entities/Reservation'
import { getOfferPersonsCount, getPackagesCartPersonsCount } from 'utils/utility'

const positiveOrZero = (value: number | null | undefined): number | null => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return null
    }
    return Math.max(0, Number(value))
}

const maxNullable = (current: number | null, next: number | null): number | null => {
    if (next === null) {
        return current
    }
    return current === null ? next : Math.max(current, next)
}

/** Angefragt — editable inquiry count stored on the lead/opportunity (`persons`). */
export const getRequestedParticipants = (lead: Lead | null | undefined): number | null => {
    return positiveOrZero(lead?.persons)
}

/**
 * Dynamic from offer: cart drivers, else calculation cart / adults+children, else input.
 */
const getOfferReservedParticipants = (offer: Offer): number | null => {
    const fromCart = getOfferPersonsCount(offer)
    if (fromCart > 0) {
        return fromCart
    }

    let fromCalculations: number | null = null
    for (const calculation of offer.calculations ?? []) {
        fromCalculations = maxNullable(fromCalculations, getCalculationReservedParticipants(calculation))
    }
    if (fromCalculations !== null && fromCalculations > 0) {
        return fromCalculations
    }

    const fromInput =
        (offer.input?.drivers_count_adult ?? 0) + (offer.input?.drivers_count_child ?? 0)
    return fromInput > 0 ? fromInput : null
}

const getCalculationReservedParticipants = (calculation: OfferCalculation): number | null => {
    const fromCalcCart = getPackagesCartPersonsCount(calculation.packagesCart)
    if (fromCalcCart > 0) {
        return fromCalcCart
    }

    const fromInput =
        (calculation.input?.drivers_count_adult ?? 0) + (calculation.input?.drivers_count_child ?? 0)
    if (fromInput > 0) {
        return fromInput
    }

    // Some API payloads still expose denormalized adults/children on calculations.
    const legacy = calculation as OfferCalculation & { adults?: number; children?: number }
    const fromLegacy = (legacy.adults ?? 0) + (legacy.children ?? 0)
    return fromLegacy > 0 ? fromLegacy : null
}

/**
 * Reserviert — always dynamic from linked offers (not stored on the lead).
 */
export const getReservedParticipants = (lead: Lead | null | undefined): number | null => {
    const offers = lead?.offers
    if (!offers?.length) {
        return null
    }

    let max: number | null = null
    for (const offer of offers) {
        max = maxNullable(max, getOfferReservedParticipants(offer))
    }
    return max
}

const getReservationEffectiveParticipants = (reservation: Reservation | null | undefined): number | null => {
    if (!reservation) {
        return null
    }
    const checked = positiveOrZero(reservation.persons_checked)
    if (checked !== null && checked > 0) {
        return checked
    }
    return positiveOrZero(reservation.persons)
}

const getReservationEffectiveRevenue = (
    reservation: Pick<Reservation, 'total_invoice'> | null | undefined
): number | null => {
    if (!reservation) {
        return null
    }

    return positiveOrZero(
        reservation.total_invoice === null || reservation.total_invoice === undefined
            ? null
            : Number(reservation.total_invoice)
    )
}

/**
 * Effektiv — use the direct reservation returned by the Opportunities endpoint.
 * Older/detail payloads may only provide the relation on linked offers, which
 * remains as a fallback.
 */
export const getEffectiveParticipants = (lead: Lead | null | undefined): number | null => {
    const fromLeadReservation = getReservationEffectiveParticipants(lead?.reservation)
    if (fromLeadReservation !== null) {
        return fromLeadReservation
    }

    const offers = lead?.offers
    if (!offers?.length) {
        return null
    }

    let max: number | null = null
    for (const offer of offers) {
        max = maxNullable(max, getReservationEffectiveParticipants(offer.reservation))
    }
    return max
}

/**
 * Effective revenue is the final invoice amount from the finished reservation
 * returned directly by the Opportunities endpoint. Nested finished offer
 * reservations remain a fallback for detail payloads.
 */
export const getEffectiveRevenue = (lead: Lead | null | undefined): number | null => {
    const fromLeadReservation = getReservationEffectiveRevenue(lead?.finishedReservation)
    if (fromLeadReservation !== null) {
        return fromLeadReservation
    }

    let max: number | null = null
    for (const offer of lead?.offers ?? []) {
        max = maxNullable(max, getReservationEffectiveRevenue(offer.finishedReservation))
    }
    return max
}

export const formatParticipantCount = (value: number | null | undefined): string => {
    if (value === null || value === undefined || value <= 0) {
        return '-'
    }
    return String(value)
}
